import type { ReactNode } from "react";
import { format as dateFormat, parse as dateParse } from "date-fns";
import {
  formatNumber,
  formatNumberAsMoney,
  isArray,
  isBoolean,
  isEmpty,
  isNumber,
  isString,
  isStringNumber,
} from "@huub-web/utils";

import type { FormInputType, DataFieldValue } from "../../types";
import { DEFAULT_DATE_FORMAT } from "../../components/DateField";
import type { FormInputProps } from "../../components/FormInput";
import { InputAddOn } from "../../components/InputAddOn";
import { Text, type TextProps } from "../../components/Text";
import type { TranslationKey } from "../../contexts";

import { countries } from "../../data/countries";

export type InputDisplayConfiguration = {
  trailing?: ReactNode;
  leading?: ReactNode;
  display?: (
    value: DataFieldValue,
    config?: {
      selectOptions?: FormInputProps["selectOptions"];
      i18n?: (key: TranslationKey) => string;
    },
  ) => ReactNode;
  placeholderKey?: TranslationKey;
  maxWidth?: string;
  minWidth?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props?: Record<string, any>;
};

export type InputDisplayConfigurationRepository = Record<
  FormInputType,
  InputDisplayConfiguration
>;

/**
 * Configuration for displaying form inputs.
 */
export const FormInputDisplayConfiguration: InputDisplayConfigurationRepository =
  {
    money: {
      leading: <InputAddOn variant="leading" icon="euro" />,
      // maxWidth: rem(286),
    },
    percentage: {
      trailing: <InputAddOn variant="trailing">%</InputAddOn>,
      // maxWidth: rem(140),
    },
    email: {
      // leading: <InputAddOn variant="leading" icon="email" />,
    },
    phone: {
      // leading: <InputAddOn variant="leading" icon="call" />,
    },
    date: {
      // leading: <InputAddOn variant="leading" icon="calendar" />,
      // placeholderKey: "general.select",
    },
    country: {
      // placeholderKey: "general.select",
    },
    select: {
      // placeholderKey: "general.select",
    },
    multiSelect: {
      // placeholderKey: "general.select",
    },
    boolean: {
      // placeholderKey: "general.select",
      // maxWidth: rem(140),
    },
    iban: {
      // leading: <InputAddOn variant="leading">IBAN</InputAddOn>,
      // maxWidth: rem(300),
    },
    kvkNumber: {
      // leading: <InputAddOn variant="leading">KvK</InputAddOn>,
      // maxWidth: rem(260),
    },
    bsn: {
      // leading: <InputAddOn variant="leading">BSN</InputAddOn>,
      // maxWidth: rem(260),
    },
    postalCode: {
      // maxWidth: rem(140), // rem(100)
    },
    number: {
      // maxWidth: rem(140),
    },
    float: {
      // maxWidth: rem(140),
    },
    idNumber: {
      // maxWidth: rem(260),
    },
    permitNumber: {
      // maxWidth: rem(260),
    },
    string: {
      // maxWidth: rem(460),
    },
    check: {},
    afmNumber: {
      // maxWidth: rem(140),
    },
    nwwiNumber: {
      // maxWidth: rem(260),
    },
    bagNumber: {
      // maxWidth: rem(260),
    },
    hdnReceivercode: {
      // maxWidth: rem(140),
    },
    kifidNumber: {
      // maxWidth: rem(140),
    },
    monthNumber: {
      // placeholderKey: "general.select",
    },
    yearNumber: {
      // placeholderKey: "general.select",
    },
    radio: {},
    checkboxes: {},
    hidden: {},
  };

export const toggleInputTextProps: Omit<TextProps, "children"> = {
  variant: "bodyL",
  noWrap: true,
  ellipsis: true,
};

/**
 * Configuration for displaying toggle inputs.
 */
export const ToggleInputDisplayConfiguration: InputDisplayConfigurationRepository =
  {
    money: {
      leading: "€",
      display: (value) => {
        const amount = isString(value) ? parseFloat(value) : value;

        return (
          <Text {...toggleInputTextProps} align="right">
            {isNumber(amount) ? formatNumberAsMoney(amount, "decimal") : "-"}
          </Text>
        );
      },
    },
    select: {
      display: (value, config) => {
        let display = "-";
        if (!config?.selectOptions) {
          console.warn("No selectOptions provided for select");
        } else if (config?.selectOptions) {
          const option = config.selectOptions.find(
            (option) => option.value?.toString() === value?.toString(),
          );
          display = option?.label || "-";
        }

        return <Text {...toggleInputTextProps}>{display ?? "-"}</Text>;
      },
      minWidth: 120,
      props: {
        popup: { placement: "bottom" },
      },
    },
    multiSelect: {
      display: (value, config) => {
        let display = "-";
        if (!config?.selectOptions) {
          console.warn("No selectOptions provided for multiSelect");
        } else if (config?.selectOptions) {
          if (isArray(value) && value.length > 0) {
            display =
              value
                .map((v) => {
                  const option = config.selectOptions?.find(
                    (option) => option.value?.toString() === v?.toString(),
                  );
                  return option?.label || "-";
                })
                .join(", ") ?? "-";
          } else if (!isArray(value)) {
            const option = config.selectOptions.find(
              (option) => option.value?.toString() === value?.toString(),
            );
            display = option?.label || "-";
          }
        }

        return <Text {...toggleInputTextProps}>{display ?? "-"}</Text>;
      },
      minWidth: 120,
      props: {
        popup: { placement: "bottom" },
      },
    },
    date: {
      display: (value, config) => {
        let dateDisplay = "-";

        try {
          if (isString(value) && value && config?.i18n) {
            try {
              const date = dateParse(value, DEFAULT_DATE_FORMAT, new Date());
              const month = config?.i18n(
                `dates.months.full.${dateFormat(date, "MMM").toLowerCase()}` as TranslationKey,
              );
              dateDisplay = `${dateFormat(date, "d")} ${month.toLocaleLowerCase()} ${date.getFullYear()}`;
            } catch (e) {
              console.warn(`Failed to parse date "${value}"`, e);
            }
          }
          if (!config?.i18n) {
            console.warn("No i18n function provided for date display");
          }
        } catch (error) {
          console.error("Failed to parse date:", error);
        }

        return <Text {...toggleInputTextProps}>{dateDisplay ?? "-"}</Text>;
      },
      props: {
        popup: { placement: "bottom" },
      },
    },
    boolean: {
      display: (value) => (
        <Text {...toggleInputTextProps}>
          {(isBoolean(value) && value === true) || value === "true"
            ? "Ja"
            : isEmpty(value)
              ? "-"
              : "Nee"}
        </Text>
      ),
      minWidth: 80,
      props: {
        popup: { placement: "bottom" },
      },
    },
    check: {},
    postalCode: {
      minWidth: 140,
    },
    number: {
      minWidth: 120,
      display: (value) => (
        <Text {...toggleInputTextProps} align="right">
          {isNumber(value) || isStringNumber(value)
            ? formatNumber(value as number | string)
            : "-"}
        </Text>
      ),
    },
    string: {
      minWidth: 260,
    },
    country: {
      display: (value) => {
        const country = countries?.[value as string];
        return (
          <Text {...toggleInputTextProps}>{country?.dutchName ?? "-"}</Text>
        );
      },
      props: {
        popup: { placement: "bottom" },
      },
    },
    float: {
      minWidth: 120,
      display: (value) => (
        <Text {...toggleInputTextProps} align="right">
          {isNumber(value) || isStringNumber(value)
            ? formatNumber(value as number | string)
            : "-"}
        </Text>
      ),
    },
    percentage: {
      trailing: "%",
      minWidth: 120,
      display: (value) => (
        <Text {...toggleInputTextProps} align="right">
          {isNumber(value) || isStringNumber(value)
            ? formatNumber(value as number | string)
            : "-"}
        </Text>
      ),
    },
    email: {
      minWidth: 280,
    },
    phone: {
      minWidth: 260,
    },
    idNumber: {
      minWidth: 220,
    },
    bsn: {
      minWidth: 220,
    },
    iban: {
      minWidth: 280,
    },
    kvkNumber: {
      minWidth: 220,
    },
    permitNumber: {
      minWidth: 220,
    },
    afmNumber: {
      minWidth: 220,
    },
    bagNumber: {
      minWidth: 280,
    },
    nwwiNumber: {
      minWidth: 280,
    },
    hdnReceivercode: {
      minWidth: 140,
    },
    kifidNumber: {
      minWidth: 220,
    },
    monthNumber: {
      display: (value, config) => {
        let dateDisplay = "-";

        try {
          if (value && config?.i18n) {
            try {
              const date = new Date();
              date.setMonth(value as number);
              dateDisplay =
                config?.i18n(
                  `dates.months.full.${dateFormat(date, "MMM").toLowerCase()}` as TranslationKey,
                ) ?? value;
            } catch (e) {
              console.warn(`Failed to parse date "${value}"`, e);
            }
          }
          if (!config?.i18n) {
            console.warn("No i18n function provided for date display");
          }
        } catch (error) {
          console.error("Failed to parse date:", error);
        }

        return <Text {...toggleInputTextProps}>{dateDisplay ?? "-"}</Text>;
      },
      minWidth: 120,
      props: {
        popup: { placement: "bottom" },
      },
    },
    yearNumber: {
      minWidth: 120,
      props: {
        popup: { placement: "bottom" },
      },
    },
    radio: {},
    checkboxes: {
      display: (value, config) => {
        let display = "-";
        if (!config?.selectOptions) {
          console.warn("No selectOptions provided for checkboxes");
        } else if (config?.selectOptions) {
          if (isArray(value) && value.length > 0) {
            display =
              value
                .map((v) => {
                  const option = config.selectOptions?.find(
                    (option) => option.value?.toString() === v?.toString(),
                  );
                  return option?.label || "-";
                })
                .join(", ") ?? "-";
          } else if (!isArray(value)) {
            const option = config.selectOptions.find(
              (option) => option.value?.toString() === value?.toString(),
            );
            display = option?.label || "-";
          }
        }

        return <Text {...toggleInputTextProps}>{display ?? "-"}</Text>;
      },
      minWidth: 120,
    },
    hidden: {},
  };
